import {
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ChangeDetectionStrategy,
    input,
    inject,
} from '@angular/core';
import { User } from '../../entities/user';
import { UserRoles } from '../../entities/user-roles';
import { AccountType } from '../../entities/account-type';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { defaultInputValidator, isEMail, phoneValidators } from '../../../common/validators/curafida-validators';
import { combineLatest, Observable, ReplaySubject } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CurafidaCardComponent } from '../../../common/components/curafida-card/curafida-card.component';
import { AsyncPipe } from '@angular/common';
import { CurafidaTextInputComponent } from '../../../common/components/curafida-input/curafida-text-input/curafida-text-input.component';
import { CurafidaSaveCancelButtonComponent } from '../../../common/components/curafida-save-cancel-button/curafida-save-cancel-button.component';
import { distinctUntilChanged, filter, finalize, map, mergeMap, startWith, take } from 'rxjs/operators';
import { UpdateEmailService } from './update-email/update-email.service';
import { Subscription } from 'rxjs/internal/Subscription';
import { ActivatedRoute, Router } from '@angular/router';
import { LoadingService } from '../../../common/services/loading/loading.service';
import { UpdateEmailResource } from './update-email/update-email.resource';
import { IonButton, IonCardContent } from '@ionic/angular';
import { TranslatePipe } from '@ngx-translate/core';
import { Buffer } from 'buffer';

@Component({
    selector: 'user-contact',
    templateUrl: './user-contact.component.html',
    styleUrls: ['./user-contact.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        CurafidaCardComponent,
        ReactiveFormsModule,
        CurafidaTextInputComponent,
        AsyncPipe,
        CurafidaSaveCancelButtonComponent,
        IonButton,
        IonCardContent,
        TranslatePipe,
    ],
})
export class UserContactComponent implements OnInit, OnDestroy {
    private readonly formBuilder = inject(FormBuilder);
    readonly updateEmailService = inject(UpdateEmailService);
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly loadingService = inject(LoadingService);

    @Output()
    readonly cancel$ = new EventEmitter();
    @Output()
    readonly save$ = new EventEmitter();
    @Output()
    readonly edit$ = new EventEmitter();
    @Output()
    readonly updateEmailVerified$ = new EventEmitter<UpdateEmailResource>();

    readonly isMobile = input<boolean>();
    readonly isNewUser = input<boolean>();
    readonly hasRightToModifyUser = input<boolean>();
    readonly isOwnDetails = input<boolean>();

    accountType$: Observable<AccountType>;

    isCurrentEmailVerified$ = new ReplaySubject<boolean>(1);

    formGroup: FormGroup;
    isUpdateEmailVisible$: Observable<boolean>;
    protected readonly UserRoles = UserRoles;
    protected readonly AccountType = AccountType;
    private accountTypeSubscription: Subscription;

    constructor() {
        this.updateEmailService.updateEmail$
            .pipe(takeUntilDestroyed())
            .subscribe((it) =>
                it?.isVerified
                    ? this.updateEmailControl?.patchValue(null)
                    : this.updateEmailControl?.patchValue(it?.email),
            );
    }

    private _user: User;

    get user() {
        return this._user;
    }

    @Input()
    set user(value: User) {
        this.formGroup?.patchValue({
            email: value?.email || '',
            phone: value?.phone || '',
            mobilePhone: value?.mobilePhone || '',
            company: value?.company || '',
        });
        if (this.user?.username !== value?.username) {
            this.updateEmailService.clear();
            if (value?.email) {
                this.updateEmailService.fetchCurrent(value.username);
            }
        }
        this._user = value;
        this.isCurrentEmailVerified$.next(value.emailVerified);
    }

    private _isEditEnabled: boolean;

    get isEditEnabled() {
        return this._isEditEnabled;
    }

    @Input()
    set isEditEnabled(value: boolean) {
        if (this.isEditEnabled !== value) {
            this.onChangeIsEditEnabled(value);
        }
        this._isEditEnabled = value;
    }

    private _rootForm: FormGroup;

    get rootForm(): FormGroup {
        return this._rootForm;
    }

    @Input()
    set rootForm(value: FormGroup) {
        if (value && this.rootForm !== value) {
            this.formGroup = this.formBuilder.group({
                email: new FormControl({ value: this.user?.email || '', disabled: !this.isEditEnabled }, [
                    Validators.email,
                    isEMail,
                ]),
                emailUpdate: new FormControl(
                    { value: this.updateEmailService.current?.email, disabled: !this.isEditEnabled },
                    [Validators.email, isEMail, this.updateEmailNotDeletable],
                ),
                phone: new FormControl(
                    { value: this.user?.phone || '', disabled: !this.isEditEnabled },
                    phoneValidators,
                ),
                mobilePhone: new FormControl(
                    {
                        value: this.user?.mobilePhone || '',
                        disabled: !this.isEditEnabled,
                    },
                    phoneValidators,
                ),
                company: new FormControl(
                    { value: this.user?.company || '', disabled: !this.isEditEnabled },
                    defaultInputValidator,
                ),
            });
            value.setControl('contact', this.formGroup);
            this.accountTypeSubscription?.unsubscribe();
            this.accountType$ = value.controls.accountType.valueChanges.pipe(
                startWith(value.controls.accountType.value),
                filter((it) => it != null),
                distinctUntilChanged(),
            );
            this.accountTypeSubscription = this.accountType$.subscribe((it) => this.onChangeAccountType(it));
            this.isUpdateEmailVisible$ = combineLatest([
                this.updateEmailControl.statusChanges.pipe(
                    startWith(this.updateEmailControl.status),
                    map((fieldStatus) => fieldStatus !== 'DISABLED'),
                ),
                this.updateEmailService.updateEmail$.pipe(
                    map((updateEmail) => Boolean(updateEmail?.email && !updateEmail?.isVerified)),
                ),
                this.activatedRoute.queryParams.pipe(map((it) => Boolean(it?.username !== 'new'))),
                this.accountType$.pipe(map((it) => it === AccountType.LOGIN_USER)),
                this.isCurrentEmailVerified$,
            ]).pipe(
                map(
                    ([
                        isNotDisabled,
                        isUpdateEmailEventRunning,
                        isExistingAccount,
                        isLoginUser,
                        isCurrentEmailVerified,
                    ]) =>
                        (isNotDisabled || isUpdateEmailEventRunning) &&
                        isExistingAccount &&
                        isLoginUser &&
                        isCurrentEmailVerified,
                ),
                distinctUntilChanged(),
            );
        }
        this._rootForm = value;
    }

    get isSaveNecessary(): boolean {
        return (
            this.updateEmailControl.dirty &&
            this.updateEmailControl.enabled &&
            this.updateEmailControl.valid &&
            this.updateEmailControl.value
        );
    }

    get currentEmailControl() {
        return this.formGroup?.get('email') as FormControl;
    }

    get updateEmailControl() {
        return this.formGroup?.get('emailUpdate') as FormControl;
    }

    ngOnInit() {
        this.onChangeIsEditEnabled(this.isEditEnabled);
        this.activatedRoute.queryParams
            .pipe(
                take(1),
                filter((it) => Boolean(it?.verifyEmail)),
                map((it) => Buffer.from(it.verifyEmail, 'base64').toString('binary').split(':')),
                mergeMap(([email, eventId]) => this.updateEmailService.verify(this.user.username, eventId, email)),
                finalize(() =>
                    this.router.navigate([], {
                        queryParams: {
                            verifyEmail: null,
                        },
                        queryParamsHandling: 'merge',
                    }),
                ),
            )
            .subscribe((updateEmailResource) => {
                this.user.email = updateEmailResource.email;
                this.user.emailVerified = true;
                this.currentEmailControl?.patchValue(updateEmailResource.email);
                this.updateEmailControl?.patchValue(null);
                this.updateEmailVerified$.emit(updateEmailResource);
            });
    }

    ngOnDestroy() {
        this.updateEmailService.clear();
        this.accountTypeSubscription?.unsubscribe();
    }

    async save() {
        this.loadingService.startLoadingModal('UPDATE_EMAIL.LOADING_INITIATION');
        return this.updateEmailService
            .initiate(this.user.username, this.updateEmailControl.value)
            .toPromise()
            .finally(() => this.loadingService.stopLoadingModal());
    }

    protected readonly updateEmailIsSameAsCurrent: ValidatorFn = () =>
        this.currentEmailControl.value === this.updateEmailControl.value ? ['UPDATE_EMAIL.SAME'] : [];

    protected readonly updateEmailIsUnchanged: ValidatorFn = () =>
        this.updateEmailService.current?.email &&
        this.updateEmailService.current?.email === this.updateEmailControl.value
            ? ['UPDATE_EMAIL.UNCHANGED']
            : [];

    protected readonly updateEmailNotDeletable: ValidatorFn = () =>
        this.updateEmailService.current?.email &&
        !this.updateEmailService.current?.isVerified &&
        !this.updateEmailControl.value
            ? ['UPDATE_EMAIL.NOT_DELETABLE']
            : [];

    private onChangeIsEditEnabled(isEditEnabled: boolean) {
        if (isEditEnabled) {
            this.formGroup?.enable();
            const isNewUser = this.isNewUser();
            if (
                isNewUser ||
                this.user?.accountType === AccountType.PLAIN_USER ||
                (this.user?.accountType === AccountType.LOGIN_USER && this.user.emailVerified === false)
            ) {
                this.currentEmailControl?.enable();
            } else {
                this.currentEmailControl?.disable();
            }
            if (!isNewUser && this.currentEmailControl?.value) {
                this.updateEmailControl?.enable();
            }
        } else {
            this.updateEmailService.current?.isVerified
                ? this.updateEmailControl?.patchValue(null)
                : this.updateEmailControl?.patchValue(this.updateEmailService.current?.email);
            this.updateEmailControl?.updateValueAndValidity();
            this.updateEmailControl?.disable();
            this.formGroup?.disable();
            this.formGroup?.markAsPristine();
        }
    }

    private onChangeAccountType(accountType: AccountType) {
        const email = this.currentEmailControl;
        if (accountType === AccountType.LOGIN_USER) {
            email.addValidators(Validators.required);
        }
        if (accountType === AccountType.PLAIN_USER) {
            email.removeValidators(Validators.required);
        }
        email.updateValueAndValidity();
    }
}
